import React, {
    useState, useEffect
} from 'react';

import {
    Settings, Building, Cctv, MapPin, Save, Plus, Trash2, CheckCircle, Shield, Bell, Info, RefreshCw, AlertTriangle, Database, Activity, Users, FileText, Share2, Copy, ExternalLink
} from 'lucide-react';

import axios from 'axios';
import TeamManagement from './TeamManagement';
import ReportsDashboard from './ReportsDashboard';

const ProjectSettings = () => {
    const [activeTab, setActiveTab] = useState('project');
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [projectData, setProjectData] = useState({
        name: '', contract_number: '', address: '', status: 'active'
    });
    // Estado de Cámaras 
    const [cameras, setCameras] = useState([]);
    const [newCamera, setNewCamera] = useState({
        name: '', location_description: '', rtsp_url: ''
    });
    const [auditLogs, setAuditLogs] = useState([]);
    const [shares, setShares] = useState([]);
    const [shareExpiry, setShareExpiry] = useState('');

    const userRole = localStorage.getItem('userRole') || 'VIEWER';
    const isDemo = userRole === 'DEMO_USER';
    const isAdmin = userRole === 'COMPANY_ADMIN' || userRole === 'SUPER_ADMIN';
    const canWrite = isAdmin || userRole === 'PROJECT_MANAGER';

    // Para controlar el modal de confirmación de Reset Demo
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [resetSuccess, setResetSuccess] = useState(false);
    const [resetError, setResetError] = useState(null);

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const tabParam = queryParams.get('tab');
        if (tabParam) {
            setActiveTab(tabParam);
        }
    }, []);

    const handleResetDemo = async () => {
        setResetLoading(true);
        setResetError(null);
        try {
            const projectId = localStorage.getItem('activeProjectId');
            const token = localStorage.getItem('token');
            const response = await axios.post('http://localhost:4000/api/demo/reset', {}, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-project-id': projectId
                }
            });
            setResetSuccess(true);
            setShowResetConfirm(false);
            // Redireccionar o recargar tras 2.5s
            setTimeout(() => {
                setResetSuccess(false);
                window.location.reload();
            }, 2500);
        } catch (error) {
            console.error("Error al restaurar demo:", error);
            setResetError(error.response?.data?.error || "Error de conexión al servidor.");
        } finally {
            setResetLoading(false);
        }
    };

    // Cargar datos iniciales        
    useEffect(() => {
        const fetchShares = async () => {
            try {
                const projectId = localStorage.getItem('activeProjectId');
                if (!projectId) return;
                const token = localStorage.getItem('token');
                const res = await axios.get(`http://localhost:4000/api/projects/${projectId}/shares`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setShares(res.data);
            } catch (err) {
                console.error("Error al obtener enlaces compartidos:", err);
            }
        };

        const fetchData = async () => {
            try {
                const projectId = localStorage.getItem('activeProjectId');
                if (!projectId) return;

                const token = localStorage.getItem('token');
                const config = { headers: { Authorization: `Bearer ${token}` } };

                const [projRes, camRes, auditRes] = await Promise.all([
                    axios.get('http://localhost:4000/api/projects', config),
                    axios.get('http://localhost:4000/api/cameras', config),
                    axios.get('http://localhost:4000/api/audit-logs', config)
                ]);

                const current = projRes.data.find(p => p.id === parseInt(projectId));
                if (current) setProjectData(current);
                else setProjectData(projRes.data[0]);

                if (current && current.id) {
                    setCameras(camRes.data);
                }
                setAuditLogs(auditRes.data);
                fetchShares();
            } catch (error) {
                console.error("Error al cargar configuración:", error);
            }
        };
        fetchData();
    }, []); // Empty dependency array means this runs once on mount

    const triggerSuccess = () => {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
    };

    // Manejar guardado de proyecto
    const handleSaveProject = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (projectData.id) {
                await axios.put(`http://localhost:4000/api/projects/${projectData.id}`, projectData);
            } else {
                await axios.post('http://localhost:4000/api/projects', projectData);
            }
            triggerSuccess();
        } catch (error) {
            console.error("Error al guardar proyecto:", error);
        } finally {
            setLoading(false);
        }
    };

    // Manejar guardado de cámara
    const handleAddCamera = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const projectId = localStorage.getItem('activeProjectId');
            if (!projectId) {
                console.error("No active project ID found.");
                return;
            }
            const response = await axios.post('http://localhost:4000/api/cameras', newCamera);
            setCameras([...cameras, response.data]);
            setNewCamera({ name: '', location_description: '', rtsp_url: '' });
            triggerSuccess();
        } catch (error) {
            console.error("Error al añadir cámara:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCamera = async (cameraId) => {
        setLoading(true);
        try {
            await axios.delete(`http://localhost:4000/api/cameras/${cameraId}`);
            setCameras(cameras.filter(cam => cam.id !== cameraId));
            triggerSuccess();
        } catch (error) {
            console.error("Error al eliminar cámara:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateShare = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const projectId = localStorage.getItem('activeProjectId');
            const token = localStorage.getItem('token');
            const res = await axios.post(`http://localhost:4000/api/projects/${projectId}/shares`, {
                expires_at: shareExpiry || null
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setShares([res.data, ...shares]);
            setShareExpiry('');
            triggerSuccess();
        } catch (error) {
            console.error("Error al crear enlace compartido:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteShare = async (shareId) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`http://localhost:4000/api/projects/shares/${shareId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setShares(shares.filter(s => s.id !== shareId));
            triggerSuccess();
        } catch (error) {
            console.error("Error al eliminar enlace compartido:", error);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert("Enlace copiado al portapapeles");
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('logo', file);

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`http://localhost:4000/api/projects/${projectData.id}/logo`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            });
            setProjectData({ ...projectData, logo_url: res.data.logo_url });
            triggerSuccess();
        } catch (error) {
            console.error("Error al subir el logotipo:", error);
            alert("Error al subir el logotipo.");
        } finally {
            setLoading(false);
        }
    };

    return (<div className="min-h-screen bg-theme-bg text-theme-text-primary p-4 md:p-8 font-sans"> {
        /* HEADER */
    } <div className="max-w-5xl mx-auto flex justify-between items-center mb-8"> <div> <h1 className="text-3xl font-extrabold tracking-tight flex items-center gap-3"> <Settings className="text-blue-500" size={
        32
    } /> Administración del Proyecto </h1> <p className="text-theme-text-secondary mt-2">Gestiona la información central, la empresa constructora y la red de cámaras.</p> </div> {
                /* Toast Notificación */
            } {
                showSuccess && (<div className="flex items-center gap-2 bg-green-500/20 text-green-400 border border-green-500 px-4 py-2 rounded-xl animate-bounce"> <CheckCircle size={
                    18
                } /> Guardado exitosamente </div>)
            } </div> <div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-8"> {
                /* SIDEBAR DE TABS */
            } <div className="w-full md:w-64 flex flex-col gap-2"> <button onClick={
                () => setActiveTab('project')
            } className={
                `flex items-center gap-3 p-4 rounded-2xl transition-all font-semibold ${activeTab === 'project' ? 'bg-blue-600 text-theme-text-primary' : 'bg-theme-panel text-theme-text-secondary hover:bg-theme-border hover:text-theme-text-primary'
                }`
            } > <Building size={
                20
            } /> Información </button>
            <button onClick={
                () => setActiveTab('cameras')
            } className={
                `flex items-center gap-3 p-4 rounded-2xl transition-all font-semibold ${activeTab === 'cameras' ? 'bg-blue-600 text-theme-text-primary' : 'bg-theme-panel text-theme-text-secondary hover:bg-theme-border hover:text-theme-text-primary'}`
            } > <Cctv size={
                20
            } /> Red de Cámaras </button> <button onClick={
                () => setActiveTab('audit')
            } className={
                `flex items-center gap-3 p-4 rounded-2xl transition-all font-semibold ${activeTab === 'audit' ? 'bg-blue-600 text-theme-text-primary' : 'bg-theme-panel text-theme-text-secondary hover:bg-theme-border hover:text-theme-text-primary'
                }`
            } > <Shield size={
                20
            } /> Auditoría </button> {isAdmin && (
                <button onClick={
                    () => setActiveTab('team')
                } className={
                    `flex items-center gap-3 p-4 rounded-2xl transition-all font-semibold ${activeTab === 'team' ? 'bg-blue-600 text-theme-text-primary' : 'bg-theme-panel text-theme-text-secondary hover:bg-theme-border hover:text-theme-text-primary'}`
                } > <Users size={
                    20
                } /> Equipo </button>
            )} <button onClick={
                () => setActiveTab('shares')
            } className={
                `flex items-center gap-3 p-4 rounded-2xl transition-all font-semibold ${activeTab === 'shares' ? 'bg-blue-600 text-theme-text-primary' : 'bg-theme-panel text-theme-text-secondary hover:bg-theme-border hover:text-theme-text-primary'}`
            } > <Share2 size={
                20
            } /> Enlaces Públicos </button> <button onClick={
                () => setActiveTab('reports')
            } className={
                `flex items-center gap-3 p-4 rounded-2xl transition-all font-semibold ${activeTab === 'reports' ? 'bg-blue-600 text-theme-text-primary' : 'bg-theme-panel text-theme-text-secondary hover:bg-theme-border hover:text-theme-text-primary'}`
            } > <FileText size={
                20
            } /> Reportes </button> </div> {
                /* CONTENIDO PRINCIPAL POR TAB */
            } <div className="flex-1"> {
                /* TAB: PROYECTO */
            } {
                    activeTab === 'project' && (<div className="bg-theme-panel border border-theme-border rounded-3xl p-8 shadow-xl"> <h2 className="text-xl font-bold mb-6 flex items-center gap-2 border-b border-theme-border pb-4"> <Building className="text-blue-400" /> Detalles de Obra </h2> <form onSubmit={
                        handleSaveProject
                    } className="space-y-6"> <div className="grid grid-cols-1 md:grid-cols-2 gap-6"> <div className="space-y-2"> <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider">Nombre del Proyecto</label> <input type="text" value={
                        projectData.name
                    } onChange={
                        (e) => setProjectData({
                            ...projectData, name: e.target.value
                        })
                    } className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-theme-text-primary focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all" placeholder="Ej. Torre Mayor, Fase 2..." required /> </div> <div className="space-y-2"> <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider">Número de Contrato</label> <input type="text" value={
                        projectData.contract_number
                    } onChange={
                        (e) => setProjectData({
                            ...projectData, contract_number: e.target.value
                        })
                    } className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-theme-text-primary focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all" placeholder="CONT-2026-089A" /> </div> </div> <div className="space-y-2"> <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider flex items-center gap-1"><MapPin size={
                        14
                    } /> Ubicación de Obra</label> <textarea value={
                        projectData.address
                    } onChange={
                        (e) => setProjectData({
                            ...projectData, address: e.target.value
                        })
                    } rows="3" className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-theme-text-primary focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all resize-none" placeholder="Dirección física completa del proyecto..." ></textarea> </div> 
                    
                    {/* Sección de Logotipo */}
                    <div className="space-y-4 bg-theme-bg/30 p-5 rounded-2xl border border-theme-border/50">
                        <div className="text-sm font-bold text-theme-text-primary">Logotipo de la Constructora</div>
                        <div className="flex flex-col sm:flex-row items-center gap-6">
                            <div className="h-20 w-36 rounded-xl border border-theme-border bg-theme-bg flex items-center justify-center overflow-hidden">
                                {projectData.logo_url ? (
                                    <img src={projectData.logo_url} alt="Logotipo Obra" className="max-h-full max-w-full object-contain" />
                                ) : (
                                    <span className="text-xs text-theme-text-secondary">Sin Logotipo</span>
                                )}
                            </div>
                            {canWrite && (
                                <div className="space-y-2 flex-1 w-full">
                                    <label className="block text-xs font-bold text-theme-text-secondary uppercase tracking-wider">Subir nuevo logotipo (.png, .jpg)</label>
                                    <input 
                                        type="file" 
                                        onChange={handleLogoUpload} 
                                        accept="image/*" 
                                        className="text-xs text-theme-text-secondary file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-blue-600/10 file:text-blue-400 hover:file:bg-blue-600/20 cursor-pointer"
                                    />
                                    <p className="text-[10px] text-theme-text-secondary">Este logotipo se mostrará en los enlaces públicos y en la marca de agua de los timelapses.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="pt-4 flex justify-end"> <button type="submit" disabled={
                        loading
                    } className="bg-blue-600 hover:bg-blue-500 text-theme-text-primary font-bold py-3 px-8 rounded-xl transition-all shadow-lg flex items-center gap-2 group disabled:opacity-50" > {
                            loading ? <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <Save size={
                                20
                            } className="group-hover:scale-110 transition-transform" />
                        } Guardar Cambios </button> </div> </form> </div>)
                } {
                    /* TAB: CÁMARAS */
                } {
                    activeTab === 'cameras' && (<div className="space-y-6"> {
                        /* Formulario Nueva Cámara */
                    } {userRole === 'SUPER_ADMIN' && (
                        <div className="bg-theme-panel border border-theme-border rounded-3xl p-8 shadow-xl"> <h2 className="text-xl font-bold mb-6 flex items-center gap-2 border-b border-theme-border pb-4"> <Plus className="text-green-400" /> Añadir Nueva Cámara </h2> <form onSubmit={
                            handleAddCamera
                        } className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end"> <div className="space-y-2 md:col-span-2"> <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider">Nombre Identificador</label> <input type="text" value={
                            newCamera.name
                        } onChange={
                            e => setNewCamera({
                                ...newCamera, name: e.target.value
                            })
                        } className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-theme-text-primary focus:ring-2 focus:ring-blue-500 focus:outline-none" placeholder="Ej. Cámara Grúa Norte" required /> </div> <div className="space-y-2"> <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider">Ubicación / Sector</label> <input type="text" value={
                            newCamera.location_description
                        } onChange={
                            e => setNewCamera({
                                ...newCamera, location_description: e.target.value
                            })
                        } className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-theme-text-primary focus:ring-2 focus:ring-blue-500 focus:outline-none" placeholder="Sector B, Piso 4" /> </div> <div className="space-y-2"> <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider">URL / RTSP Stream</label> <input type="text" value={
                            newCamera.rtsp_url
                        } onChange={
                            e => setNewCamera({
                                ...newCamera, rtsp_url: e.target.value
                            })
                        } className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-theme-text-primary focus:ring-2 focus:ring-blue-500 focus:outline-none" placeholder="rtsp://192.168.1.100..." /> </div> <div className="md:col-span-2 flex justify-end mt-2"> <button type="submit" disabled={
                            loading
                        } className="bg-green-600 hover:bg-green-500 text-theme-text-primary font-bold py-3 px-6 rounded-xl transition-all flex items-center gap-2"> {
                                loading ? <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <Plus size={
                                    20
                                } />
                            } Registrar Equipo </button> </div> </form> </div>
                    )} {
                            /* Lista de Cámaras Existentes */
                        } <div className="bg-theme-panel border border-theme-border rounded-3xl p-8 shadow-xl"> <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-theme-text-primary"> <Cctv size={
                            18
                        } /> Red Configurada ({
                                cameras.length
                            }) </h3> {
                                cameras.length === 0 ? (<div className="text-center py-8 text-theme-text-secondary border-2 border-dashed border-theme-border rounded-2xl"> No hay cámaras registradas en este proyecto. </div>) : (<div className="grid grid-cols-1 gap-3"> {
                                    cameras.map(cam => (<div key={
                                        cam.id
                                    } className="bg-theme-bg border border-theme-border p-4 rounded-xl flex justify-between items-center group hover:border-blue-500/50 transition-colors"> <div> <div className="flex items-center gap-2"> <div className={`h-2 w-2 rounded-full ${cam.status === 'offline' ? 'bg-red-500' : 'bg-green-500 animate-pulse'}`}></div> <h4 className="font-bold text-theme-text-primary">{
                                        cam.name
                                    } <span className="text-xs font-mono text-theme-text-secondary bg-theme-bg border border-theme-border px-1.5 py-0.5 rounded ml-1">ID: {cam.id}</span></h4> </div> <p className="text-sm text-theme-text-secondary mt-1 flex items-center gap-1"><MapPin size={
                                        12
                                    } /> {
                                                    cam.location_description || 'Ubicación sin definir'
                                                }
                                            </p>
                                        </div>
                                        {userRole === 'SUPER_ADMIN' && (
                                            <button onClick={() => handleDeleteCamera(cam.id)} className="p-2 text-theme-text-secondary hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"> <Trash2 size={18} /> </button>
                                        )}
                                    </div>
                                    ))
                                }
                                </div>)
                            }
                        </div>
                    </div>)
                }
                {
                    /* TAB: AUDITORIA */
                } {
                    activeTab === 'audit' && (
                        <div className="bg-theme-panel border border-theme-border rounded-3xl p-8 shadow-xl">
                            <h2 className="text-xl font-bold mb-6 flex items-center gap-2 border-b border-theme-border pb-4">
                                <Shield className="text-blue-400" /> Registro de Auditoría
                            </h2>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm text-theme-text-secondary">
                                    <thead className="bg-theme-bg/50 text-theme-text-primary">
                                        <tr>
                                            <th className="p-4 font-semibold rounded-tl-xl text-theme-text-primary">Fecha</th>
                                            <th className="p-4 font-semibold text-theme-text-primary">Usuario</th>
                                            <th className="p-4 font-semibold text-theme-text-primary">Acción</th>
                                            <th className="p-4 font-semibold text-theme-text-primary">Entidad</th>
                                            <th className="p-4 font-semibold rounded-tr-xl text-theme-text-primary">IP</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-700">
                                        {auditLogs.map((log) => (
                                            <tr key={log.id} className="hover:bg-theme-border/30 transition-colors">
                                                <td className="p-4">{new Date(log.created_at).toLocaleString()}</td>
                                                <td className="p-4">
                                                    <div className="font-medium text-theme-text-primary">{log.user_name}</div>
                                                    <div className="text-xs">{log.user_email}</div>
                                                </td>
                                                <td className="p-4">
                                                    <span className={`px-2 py-1 rounded text-xs font-bold ${log.action === 'DELETE' ? 'bg-red-500/20 text-red-500' : 'bg-blue-500/20 text-blue-500'}`}>
                                                        {log.action}
                                                    </span>
                                                </td>
                                                <td className="p-4 font-medium">{log.entity_type} <span className="text-theme-text-secondary">#{log.entity_id}</span></td>
                                                <td className="p-4 text-xs font-mono">{log.ip_address}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {auditLogs.length === 0 && (
                                    <div className="text-center py-8 text-theme-text-secondary">No hay registros de auditoría disponibles.</div>
                                )}
                            </div>
                        </div>
                    )
                }

                {
                    activeTab === 'demo' && userRole === 'SUPER_ADMIN' && (
                        <div className="bg-theme-panel border border-theme-border rounded-3xl p-8 shadow-xl relative overflow-hidden">
                            {/* Fondo decorativo premium con brillo */}
                            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl pointer-events-none"></div>
                            
                            <h2 className="text-xl font-bold mb-6 flex items-center gap-2 border-b border-theme-border pb-4">
                                <RefreshCw className="text-blue-400 animate-spin-slow" /> Entorno Demo & Simulación
                            </h2>

                            {/* Alerta de Modo de Lectura si es Demo User */}
                            {!isAdmin ? (
                                <div className="p-5 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl flex items-start gap-4 mb-6">
                                    <AlertTriangle className="text-yellow-500 shrink-0 mt-0.5" size={24} />
                                    <div>
                                        <h4 className="font-bold text-yellow-500 text-sm">Modo de Demostración Activo (Lectura)</h4>
                                        <p className="text-xs text-theme-text-secondary mt-1 leading-relaxed">
                                            Tu sesión actual (`Invitado Demo`) tiene permisos restringidos. Para restaurar la base de datos o reiniciar la simulación del proyecto de construcción, se requiere ingresar con una cuenta de Administrador de la Empresa (e.g. `admin@gcarso.com`).
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-5 bg-blue-500/10 border border-blue-500/30 rounded-2xl flex items-start gap-4 mb-6">
                                    <Info className="text-blue-400 shrink-0 mt-0.5" size={24} />
                                    <div>
                                        <h4 className="font-bold text-blue-400 text-sm">Restauración de Fábrica del Demo</h4>
                                        <p className="text-xs text-theme-text-secondary mt-1 leading-relaxed">
                                            Al restaurar el entorno demo, todos los datos generados durante la sesión actual para este proyecto (registros de asistencia de obreros, avances de obra, reportes diarios en la bitácora y alertas de EHS simuladas) se borrarán de forma segura, reiniciando la base de datos a los valores de semilla por defecto.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Status Indicators */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                                <div className="bg-theme-bg/40 border border-theme-border/60 p-5 rounded-2xl flex items-center gap-4">
                                    <Database className="text-blue-400" size={24} />
                                    <div>
                                        <div className="text-xs text-theme-text-secondary font-semibold uppercase tracking-wider">Base de Datos</div>
                                        <div className="text-sm font-bold text-theme-text-primary mt-0.5 flex items-center gap-2">
                                            PostgreSQL 16 <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-bold">ONLINE</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-theme-bg/40 border border-theme-border/60 p-5 rounded-2xl flex items-center gap-4">
                                    <Activity className="text-purple-400" size={24} />
                                    <div>
                                        <div className="text-xs text-theme-text-secondary font-semibold uppercase tracking-wider">Simulador Telemetría IA/IoT</div>
                                        <div className="text-sm font-bold text-theme-text-primary mt-0.5 flex items-center gap-2">
                                            Edge Simulator <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-bold">ACTIVO</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Acciones */}
                            <div className="flex flex-col gap-4 border-t border-theme-border/50 pt-6">
                                <div className="flex justify-between items-center flex-col sm:flex-row gap-4">
                                    <div>
                                        <h3 className="font-bold text-theme-text-primary text-base">Re-inicializar Base de Datos</h3>
                                        <p className="text-xs text-theme-text-secondary mt-1">Borra registros creados y restablece las fases de Gantt y catálogos de conceptos semilla.</p>
                                    </div>
                                    <button 
                                        onClick={() => setShowResetConfirm(true)} 
                                        disabled={!isAdmin} 
                                        className={`font-bold py-3 px-6 rounded-xl transition-all shadow-lg flex items-center gap-2 select-none ${
                                            isAdmin 
                                            ? 'bg-red-600 hover:bg-red-500 text-white cursor-pointer active:scale-95' 
                                            : 'bg-red-950/20 text-red-500/40 border border-red-500/10 cursor-not-allowed'
                                        }`}
                                    >
                                        <RefreshCw size={18} />
                                        Restaurar Entorno Demo
                                    </button>
                                </div>
                            </div>

                            {/* Modal de confirmación premium */}
                            {showResetConfirm && (
                                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                                    <div className="bg-[#0b0c10] border border-theme-border max-w-md w-full rounded-3xl p-6 shadow-2xl relative">
                                        <div className="flex justify-center mb-4 text-red-500">
                                            <AlertTriangle size={48} className="animate-pulse" />
                                        </div>
                                        <h3 className="text-lg font-bold text-theme-text-primary text-center">¿Confirmar Restauración del Demo?</h3>
                                        <p className="text-sm text-theme-text-secondary text-center mt-2 leading-relaxed">
                                            Esta acción borrará de forma irreversible todos los avances, bitácoras, incidentes y timelapses generados para este proyecto en la base de datos, restaurando el estado original de semilla.
                                        </p>
                                        {resetError && (
                                            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-xs text-red-500 font-semibold text-center">
                                                {resetError}
                                            </div>
                                        )}
                                        <div className="flex gap-3 mt-6">
                                            <button 
                                                onClick={() => setShowResetConfirm(false)} 
                                                disabled={resetLoading}
                                                className="flex-1 bg-theme-panel hover:bg-theme-border text-theme-text-primary font-bold py-3 rounded-xl transition-colors border border-theme-border"
                                            >
                                                Cancelar
                                            </button>
                                            <button 
                                                onClick={handleResetDemo} 
                                                disabled={resetLoading}
                                                className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                                            >
                                                {resetLoading ? (
                                                    <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                ) : "Sí, Restaurar"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Toast / Notificación de éxito flotante */}
                            {resetSuccess && (
                                <div className="fixed bottom-6 right-6 bg-green-500 text-white px-6 py-4 rounded-2xl shadow-xl flex items-center gap-3 z-50 animate-bounce">
                                    <CheckCircle size={24} />
                                    <div>
                                        <div className="font-bold text-sm">¡Entorno Restaurado!</div>
                                        <div className="text-[10px] opacity-90">Recargando base de datos y vistas de obra...</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                } {
                    /* TAB: EQUIPO */
                } {
                    activeTab === 'team' && isAdmin && (
                        <TeamManagement inline={true} />
                    )
                } {
                    /* TAB: ENLACES COMPARTIDOS */
                } {
                    activeTab === 'shares' && (
                        <div className="space-y-6">
                            {canWrite && (
                                <div className="bg-theme-panel border border-theme-border rounded-3xl p-8 shadow-xl">
                                    <h2 className="text-xl font-bold mb-6 flex items-center gap-2 border-b border-theme-border pb-4">
                                        <Share2 className="text-blue-400" /> Generar Enlace Público Compartible
                                    </h2>
                                    <form onSubmit={handleCreateShare} className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider">Fecha de Expiración (Opcional)</label>
                                            <input 
                                                type="date" 
                                                value={shareExpiry} 
                                                onChange={e => setShareExpiry(e.target.value)} 
                                                className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-theme-text-primary focus:ring-2 focus:ring-blue-500 focus:outline-none" 
                                            />
                                        </div>
                                        <div>
                                            <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 text-theme-text-primary font-bold py-3 px-6 rounded-xl transition-all flex items-center justify-center gap-2">
                                                {loading ? <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <Plus size={20} />}
                                                Generar Enlace
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            <div className="bg-theme-panel border border-theme-border rounded-3xl p-8 shadow-xl">
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-theme-text-primary">
                                    <Share2 size={18} /> Enlaces Generados ({shares.length})
                                </h3>
                                {shares.length === 0 ? (
                                    <div className="text-center py-8 text-theme-text-secondary border-2 border-dashed border-theme-border rounded-2xl">
                                        No se han generado enlaces públicos para este proyecto.
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 gap-3">
                                        {shares.map(share => {
                                            const publicUrl = `${window.location.origin}/public/share/${share.token}`;
                                            return (
                                                <div key={share.id} className="bg-theme-bg border border-theme-border p-4 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-semibold text-theme-text-primary truncate">{publicUrl}</div>
                                                        <div className="text-xs text-theme-text-secondary mt-1 flex flex-wrap gap-x-4 gap-y-1">
                                                            <span>Creado: {new Date(share.created_at).toLocaleDateString()}</span>
                                                            {share.expires_at ? (
                                                                <span className="text-yellow-500">Expira: {new Date(share.expires_at).toLocaleDateString()}</span>
                                                            ) : (
                                                                <span className="text-green-500">Permanente</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 self-end md:self-auto">
                                                        <button 
                                                            onClick={() => copyToClipboard(publicUrl)} 
                                                            className="p-2 text-theme-text-secondary hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors" 
                                                            title="Copiar Enlace"
                                                        >
                                                            <Copy size={16} />
                                                        </button>
                                                        <a 
                                                            href={publicUrl} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer" 
                                                            className="p-2 text-theme-text-secondary hover:text-green-400 hover:bg-green-500/10 rounded-lg transition-colors" 
                                                            title="Abrir Enlace"
                                                        >
                                                            <ExternalLink size={16} />
                                                        </a>
                                                        {canWrite && (
                                                            <button 
                                                                onClick={() => handleDeleteShare(share.id)} 
                                                                className="p-2 text-theme-text-secondary hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors" 
                                                                title="Eliminar Enlace"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                } {
                    /* TAB: REPORTES */
                } {
                    activeTab === 'reports' && (
                        <ReportsDashboard inline={true} />
                    )
                }
            </div>
        </div>
    </div>
    );
};
export default ProjectSettings;
